import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';

import ServiceDateTemplate from '../component/service-date-template';
import ServiceIndividualCreate from '../component/service/individual/create';
import ServiceIndividualUpdate from '../component/service/individual/update';
import { subscribe, unsubscribe } from '../context/events';
import { useSite } from '../context/site';
import { MSG, PLeft } from '../shared';

function ServicesIndividualPage() {
    const { GetServicesIndividual } = useSite();

    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [services, setServices] = useState([]);
    const [limit, setLimit] = useState(5);
    const [skip, setSkip] = useState(0);
    const [sortField, setSortField] = useState('dateService');
    const [sortOrder, setSortOrder] = useState(-1);
    const [filters, setFilters] = useState([]);
    const [sortData, setSortData] = useState({ dateService: -1 });
    const [recordCount, setRecordCount] = useState(0);
    const [filteredRecordCount, setFilteredRecordCount] = useState(0);
    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});

    const getItems = useCallback(
        (filters, sortData, skip, limit, reset) => {
            setLoading(true);

            const data = {
                filters: Array.isArray(filters) ? filters : 0,
                skip: skip ? skip : 0,
                limit: limit ? limit : 0,
                sorts: sortData ? sortData : {}
            };

            GetServicesIndividual(data, (res) => {
                if (res.status === 200) {
                    setServices(res.data.servicesIndividual);
                    setRecordCount(res.data.count);
                    setFilteredRecordCount(res.data.filterCount);
                }

                if (!!reset) {
                    setFilters(filters);
                    setSortData(sortData);
                    setSortField('');
                    setSortOrder(1);
                    setSkip(skip);
                    setLimit(limit);
                }

                setLoading(false);
            });
        },
        [GetServicesIndividual]
    );

    const processEvent = useCallback(
        (event) => {
            const { detail: res, type: action } = event;

            if (res.status === 200) {
                if (action === MSG.create.service.individual) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Individual Service Created',
                        detail: 'New individual service created',
                        life: 3000
                    });
                }

                if (action === MSG.update.service.individual) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Individual Service Updated',
                        detail: 'Individual service updated',
                        life: 3000
                    });

                    const serviceFound = services.find((s) => s._id === res.data.service._id);

                    if (serviceFound) {
                        setServices((prev) => {
                            return [
                                ...prev.map((p) => {
                                    if (p._id === serviceFound._id) {
                                        return {
                                            ...res.data.service
                                        };
                                    }

                                    return p;
                                })
                            ];
                        });
                    }
                }
            }
        },
        [services]
    );

    const ActionsDisplay = (service) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(service);
                        setOpenUpdate(true);
                    }}
                />
            </>
        );
    };

    const parentTemplate = (service) => {
        if (!service?.parent?.nameFirst) {
            return '';
        }

        return `${service.parent.nameFirst} ${service.parent.nameLast}`;
    };

    const NotesDisplay = (service) => {
        if (!service?.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: service.notes }}></span>;
    };

    // App Events
    useEffect(() => {
        subscribe(MSG.create.service.individual, processEvent);
        subscribe(MSG.update.service.individual, processEvent);

        return () => {
            unsubscribe(MSG.create.service.individual, processEvent);
            unsubscribe(MSG.update.service.individual, processEvent);
        };
    }, [processEvent]);

    // Pagination
    useEffect(() => {
        getItems(filters, sortData, skip, limit, false);
    }, [skip, limit, filters, sortData, getItems]);

    return (
        <Card title="Individual Services" className="pageCard">
            <div className="grid">
                <div className="col">
                    <Button
                        icon="pi pi-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreate(true);
                        }}
                    />

                    <Button
                        className="ml-1"
                        icon="pi pi-refresh"
                        rounded
                        severity="help"
                        onClick={() => {
                            getItems(filters, sortData, skip, limit, false);
                        }}
                    />
                </div>
            </div>

            <DataTable
                value={services}
                emptyMessage="No services found"
                showGridlines
                removableSort
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                onSort={(event) => {
                    const sortData = {};

                    sortData[event.sortField] = event.sortOrder;

                    setSortField(event.sortField);
                    setSortOrder(event.sortOrder);

                    if (event.sortField) {
                        setSortData({ ...sortData });
                    } else {
                        setSortData({});
                    }
                }}
            >
                <Column
                    field="dateService"
                    body={ServiceDateTemplate}
                    header="Service Date"
                    sortable
                ></Column>
                <Column field="employee.name" header="Employee"></Column>
                <Column body={parentTemplate} header="Parent"></Column>
                <Column field="serviceType.itemName" header="Service Type"></Column>
                <Column field="numberOfChildren" header="Children Count" sortable></Column>
                <Column body={NotesDisplay} header="Notes"></Column>
                <Column
                    header="Actions"
                    bodyStyle={{ width: '150px' }}
                    body={ActionsDisplay}
                ></Column>
            </DataTable>

            <Paginator
                alwaysShow={true}
                leftContent={
                    <PLeft
                        filteredRecordCount={filteredRecordCount}
                        recordCount={recordCount}
                        skip={skip}
                        limit={limit}
                    />
                }
                first={skip}
                rows={limit}
                totalRecords={filters.length > 0 ? filteredRecordCount : recordCount}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={(pageEvent) => {
                    setSkip(pageEvent.first);
                    setLimit(pageEvent.rows);
                }}
            />

            {!openCreate ? null : (
                <ServiceIndividualCreate
                    open={openCreate}
                    onClose={(res) => {
                        if (res.status === 200) {
                            getItems(filters, sortData, skip, limit, false);
                        }

                        setOpenCreate(false);
                    }}
                />
            )}

            {!openUpdate ? null : (
                <ServiceIndividualUpdate
                    open={openUpdate}
                    data={updateData}
                    onClose={(res) => {
                        setUpdateData({});
                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Card>
    );
}

export default ServicesIndividualPage;
